import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// post-client-mod -- 文章列表页（公开）
//
// 路径：/post
// 给访客浏览已发布的文章列表，支持分页。
// 支持从 URL 查询参数过滤：?categorySlug=xxx 或 ?tagSlugs=xxx
// ============================================================================
import * as React from 'react';
import { useRoute } from '@hvsin/mod-route';
import { usePosts } from '../hooks/use-posts.js';
import { getMarkdownExcerpt } from '../util/markdown.js';
/**
 * 博客首页 — 公开文章列表（支持分类/标签过滤）
 */
export function PostListPage(props) {
    const { initialData } = props;
    const { Link, useLocation, useNavigate } = useRoute();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = React.useState('');
    // 从 URL 查询参数读取过滤条件（使用 useMemo 避免每次渲染创建新引用）
    const categorySlug = React.useMemo(() => new URLSearchParams(location.search).get('categorySlug') || undefined, [location.search]);
    const tagSlug = React.useMemo(() => new URLSearchParams(location.search).get('tagSlugs') || undefined, [location.search]);
    // 稳定 tagSlugs 数组引用，避免因新数组导致 usePosts 无限重渲染
    const tagSlugs = React.useMemo(() => (tagSlug ? [tagSlug] : undefined), [tagSlug]);
    const filterDesc = categorySlug
        ? `分类: ${categorySlug}`
        : tagSlug
            ? `标签: ${tagSlug}`
            : '文章列表';
    const { posts, pagination, loading, error, setPage } = usePosts({
        status: 'published',
        pageSize: 10,
        initialData: categorySlug || tagSlug ? undefined : initialData,
        categorySlug,
        tagSlugs,
    });
    const handleClearFilter = () => {
        window.location.href = '/post';
    };
    // 格式化日期
    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };
    // 生成摘要（从 Markdown 提取纯文本，截取前 160 字符）
    const getExcerpt = (content) => {
        return getMarkdownExcerpt(content, 160);
    };
    return (_jsxs("div", { style: styles.page, children: [_jsx("header", { style: styles.header, children: _jsx("div", { style: styles.headerContent, children: _jsx("h1", { style: styles.siteTitle, children: filterDesc }) }) }), _jsxs("nav", { style: styles.nav, children: [_jsx(Link, { to: "/post", style: styles.navLink, children: "\u5168\u90E8\u6587\u7AE0" }), _jsx(Link, { to: "/categories", style: styles.navLink, children: "\u5206\u7C7B" }), _jsx(Link, { to: "/tags", style: styles.navLink, children: "\u6807\u7B7E" }), _jsx("div", { style: styles.navSpacer }), _jsx("form", { onSubmit: (e) => { e.preventDefault(); if (searchInput.trim())
                            navigate(`/search?q=${encodeURIComponent(searchInput.trim())}`); }, style: styles.searchForm, children: _jsx("input", { type: "text", value: searchInput, onChange: (e) => setSearchInput(e.target.value), placeholder: "\u641C\u7D22\u6587\u7AE0...", style: styles.searchInput }) })] }), (categorySlug || tagSlug) && (_jsxs("div", { style: styles.filterBar, children: [_jsxs("span", { style: styles.filterText, children: ["\u6B63\u5728\u663E\u793A", categorySlug && _jsxs("span", { style: styles.filterBadge, children: ["\u5206\u7C7B: ", categorySlug] }), tagSlug && _jsxs("span", { style: styles.filterBadge, children: ["\u6807\u7B7E: ", tagSlug] }), "\u7684\u6587\u7AE0"] }), _jsx("button", { onClick: handleClearFilter, style: styles.clearFilterButton, children: "\u6E05\u9664\u7B5B\u9009" })] })), _jsxs("main", { style: styles.main, children: [loading && _jsx("div", { style: styles.loading, children: "\u52A0\u8F7D\u4E2D..." }), error && _jsx("div", { style: styles.error, children: error }), !loading && !error && posts.length === 0 && (_jsx("div", { style: styles.empty, children: _jsx("p", { style: styles.emptyText, children: categorySlug || tagSlug ? '该筛选条件下暂无文章' : '暂无文章' }) })), !loading &&
                        !error &&
                        posts.map(post => (_jsxs("article", { style: styles.article, children: [_jsx("h2", { style: styles.articleTitle, children: _jsx(Link, { to: `/post/${post.id}`, style: styles.articleLink, children: post.title }) }), _jsxs("div", { style: styles.articleMeta, children: [_jsx("time", { style: styles.articleDate, children: formatDate(post.createdAt) }), post.categorySlug && (_jsx(Link, { to: `/post?categorySlug=${encodeURIComponent(post.categorySlug)}`, style: styles.categoryBadge, children: post.categorySlug })), post.tagSlugs?.map(slug => (_jsxs(Link, { to: `/post?tagSlugs=${encodeURIComponent(slug)}`, style: styles.tagBadge, children: ["#", slug] }, slug)))] }), _jsx("p", { style: styles.articleExcerpt, children: getExcerpt(post.content) }), _jsx(Link, { to: `/post/${post.id}`, style: styles.readMore, children: "\u9605\u8BFB\u5168\u6587 \u2192" })] }, post.id))), !loading && pagination && pagination.totalPages > 1 && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { onClick: () => setPage(Math.max(1, pagination.page - 1)), disabled: pagination.page <= 1, style: styles.pageButton, children: "\u2190 \u4E0A\u4E00\u9875" }), _jsxs("span", { style: styles.pageInfo, children: [pagination.page, " / ", pagination.totalPages] }), _jsx("button", { onClick: () => setPage(Math.min(pagination.totalPages, pagination.page + 1)), disabled: pagination.page >= pagination.totalPages, style: styles.pageButton, children: "\u4E0B\u4E00\u9875 \u2192" })] }))] }), _jsx("footer", { style: styles.footer, children: _jsx("p", { style: styles.footerText, children: "\u00A9 2026 Blog. All rights reserved." }) })] }));
}
const styles = {
    page: {
        minHeight: '100vh',
        backgroundColor: '#fafafa',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#1a1a1a',
    },
    header: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #eaeaea',
        padding: '60px 24px 40px',
        textAlign: 'center',
    },
    headerContent: {
        maxWidth: '720px',
        margin: '0 auto',
    },
    siteTitle: {
        margin: '0 0 8px 0',
        fontSize: '32px',
        fontWeight: 700,
        color: '#1a1a1a',
        letterSpacing: '-0.02em',
    },
    nav: {
        maxWidth: '720px',
        margin: '0 auto',
        padding: '16px 24px 0',
        display: 'flex',
        gap: '16px',
    },
    navLink: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#6366f1',
        textDecoration: 'none',
        padding: '6px 12px',
        borderRadius: '6px',
        backgroundColor: '#eef2ff',
        transition: 'background-color 0.15s ease',
    },
    navSpacer: {
        flex: 1,
    },
    searchForm: {
        display: 'flex',
    },
    searchInput: {
        padding: '6px 12px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        width: '160px',
        backgroundColor: '#ffffff',
        transition: 'border-color 0.15s ease',
    },
    filterBar: {
        maxWidth: '720px',
        margin: '12px auto 0',
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    filterText: {
        fontSize: '14px',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    filterBadge: {
        display: 'inline-block',
        padding: '2px 10px',
        backgroundColor: '#eef2ff',
        color: '#6366f1',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: 500,
    },
    clearFilterButton: {
        padding: '4px 12px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '13px',
        color: '#ef4444',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    main: {
        maxWidth: '720px',
        margin: '0 auto',
        padding: '24px 24px 60px',
    },
    loading: {
        padding: '80px 0',
        textAlign: 'center',
        color: '#999',
        fontSize: '15px',
    },
    error: {
        padding: '16px',
        backgroundColor: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '8px',
        color: '#c53030',
        fontSize: '14px',
    },
    empty: {
        padding: '80px 0',
        textAlign: 'center',
    },
    emptyText: {
        fontSize: '16px',
        color: '#999',
    },
    article: {
        padding: '32px 0',
        borderBottom: '1px solid #eaeaea',
    },
    articleTitle: {
        margin: '0 0 12px 0',
        fontSize: '22px',
        fontWeight: 600,
        lineHeight: '1.4',
    },
    articleLink: {
        color: '#1a1a1a',
        textDecoration: 'none',
        transition: 'color 0.2s ease',
    },
    articleMeta: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px',
    },
    categoryBadge: {
        padding: '2px 8px',
        backgroundColor: '#f0fdf4',
        color: '#166534',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
        textDecoration: 'none',
    },
    tagBadge: {
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        textDecoration: 'none',
    },
    articleDate: {
        fontSize: '14px',
        color: '#999',
    },
    articleExcerpt: {
        margin: '0 0 16px 0',
        fontSize: '15px',
        lineHeight: '1.7',
        color: '#555',
    },
    readMore: {
        fontSize: '14px',
        color: '#6366f1',
        textDecoration: 'none',
        fontWeight: 500,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '48px',
        paddingTop: '24px',
    },
    pageButton: {
        padding: '8px 16px',
        border: '1px solid #eaeaea',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '14px',
        color: '#555',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    pageInfo: {
        fontSize: '14px',
        color: '#999',
    },
    footer: {
        textAlign: 'center',
        padding: '40px 24px',
        borderTop: '1px solid #eaeaea',
    },
    footerText: {
        margin: 0,
        fontSize: '13px',
        color: '#999',
    },
};
//# sourceMappingURL=post-list-page.js.map